import type {
  RangeDecoration,
  WorkbookRange,
} from "@/components/common/ExcelViewer/rangeDecorations"
import {
  SOURCE_COLOR_COUNT,
  sourceColorVar,
} from "@/components/common/PdfViewer/DeclarationDocumentViewer"

/**
 * THE WORKBOOK'S PROOF ADAPTER — one adapter per flow (Concept "Excel
 * declarations and cell evidence", Part VII · "App side: viewer additive, one
 * adapter per flow"). The cell analogue of `buildSourceRegions` + `scopeProofs`:
 * proof points in, marks in workbook coordinates out.
 *
 * PURE, and free of the i18n hook — every label is supplied by the caller, the
 * same contract `scopeProofSources` keeps.
 *
 * Three things it exists to get right:
 *
 *  - **Colour is the statement's, not the proof's.** `colorIndex` comes from the
 *    statement's position in the flow's list, through the SAME eight-colour
 *    palette the PDF tints use, so a statement keeps its colour across steps and
 *    across modalities.
 *  - **Position is a surface ordinal, and display is a sheet NAME.** The `page`
 *    field was redefined, not doubled (ingest contract 2). The ordinal indexes
 *    the document's stored surface catalog; the catalog is authoritative, the
 *    point's own `sheet` is the fallback, and when neither answers, the source
 *    says so instead of inventing "page 3".
 *  - **Provenance is on the mark.** `read_by` and `hidden` ride the badge, so how
 *    an answer was produced and whether the supplier had hidden it are never
 *    inferred (Part II, Part IX).
 */

/**
 * The proof-point fields a workbook mark needs. Structural on purpose: the read
 * model's `CellQuote` satisfies it, and so will a statement's evidence anchor
 * once step 1 runs over a workbook.
 */
export type CellProofPoint = {
  /** `"cell"` marks a workbook range. Anything else is not ours. */
  kind?: string
  /** 1-based surface ordinal (the redefined `page`). */
  page: number
  /** Sheet name as the workbook spells it. The catalog outranks it. */
  sheet?: string
  /** A1 range, merge-snapped and canonicalized at ingest. */
  ref?: string
  /** `"code"` or `"model"` — how the value was read. */
  read_by?: string
  /** The supplier had hidden this content. */
  hidden?: boolean
  verified?: boolean
  producer?: string
  summary?: string
}

/** One entry of the document's persisted surface catalog. */
export type DocumentSurface = {
  /** 1-based, like a page. */
  ordinal: number
  name: string
  state?: "visible" | "hidden" | "veryHidden" | string
  hidden?: boolean
  kind?: string
  rows?: number
  cols?: number
  ref?: string
}

/** The minimal statement shape the workbook marks need (mirrors `SourceStatement`). */
export interface WorkbookSourceStatement {
  id: string
  evidence: CellProofPoint[]
  /**
   * HOW MANY KEYS `buildSourceRegions` ALREADY MINTED FOR THIS STATEMENT, from
   * its `evidence` (quote) list — the other half of the per-statement key space
   * this statement's cells continue rather than restart. Absent ⇒ 0, which is
   * every call site today: a statement's quote-evidence and cell-evidence are
   * mutually exclusive by document kind, so the two lists never actually share a
   * statement. That is an invariant of the DATA, not of this function, and this
   * offset is what stops a future statement carrying both from silently
   * colliding two different citations onto one key (`${id}#0`) — see
   * `buildSourceRegions`.
   */
  evidenceCount?: number
}

/** One proof point, resolved into something the viewer and the bar can both use. */
export interface WorkbookSource {
  /** Unique per (statement, point). */
  key: string
  statementId: string
  /** The statement title — the same one every other surface shows. */
  label: string
  colorIndex: number
  /** The surface ordinal, kept as the sort and identity key. */
  ordinal: number
  /**
   * The sheet to mark and to name. Empty exactly when `sheetKnown` is false.
   */
  sheet: string
  /**
   * False ⇒ neither the surface catalog nor the point itself named a sheet, so
   * this proof cannot be placed. The caller renders the explicit "not provided by
   * the current pipeline" state — never a crash, never a silent blank, and never
   * a made-up "page N".
   */
  sheetKnown: boolean
  /** 0-based inclusive. Null ⇒ `ref` was absent or unreadable. */
  range: WorkbookRange | null
  /** The A1 range as published, for display. */
  ref: string
  /** `"code"` | `"model"`, when the run published it. */
  readBy?: string
  /** The supplier had hidden this content. */
  hidden: boolean
  producer?: string
  verified?: boolean
  summary?: string
  /** The source-track grouping unit. Absent ⇒ the statement (step 1). */
  itemKey?: string
}

/** Is this point a workbook range? */
export function isCellProof(point: CellProofPoint): boolean {
  return point.kind === "cell"
}

const A1_CELL = /^\$?([A-Za-z]{1,3})\$?([0-9]{1,7})$/

const columnIndexOf = (letters: string): number => {
  let index = 0
  for (const char of letters.toUpperCase()) {
    index = index * 26 + (char.charCodeAt(0) - 64)
  }
  return index - 1
}

/**
 * A1 → a 0-based inclusive rectangle. `"B2"` is the degenerate single cell,
 * `"B2:D5"` the general case, and `$` markers are tolerated because a
 * canonicalized ref has none but a hand-written one might.
 *
 * Answers null for anything it cannot read, which the caller shows as a gap. It
 * never repairs and never guesses: an address either parses or it does not.
 */
export function parseA1Range(ref: string | undefined): WorkbookRange | null {
  if (!ref) return null
  const [from, to, ...rest] = ref.trim().split(":")
  if (rest.length > 0) return null
  const start = A1_CELL.exec(from ?? "")
  const end = to === undefined ? start : A1_CELL.exec(to)
  if (!start || !end) return null

  const rows = [Number(start[2]) - 1, Number(end[2]) - 1]
  const columns = [columnIndexOf(start[1]), columnIndexOf(end[1])]
  if (rows.some((r) => r < 0) || columns.some((c) => c < 0)) return null
  return {
    startRow: Math.min(rows[0], rows[1]),
    endRow: Math.max(rows[0], rows[1]),
    startColumn: Math.min(columns[0], columns[1]),
    endColumn: Math.max(columns[0], columns[1]),
  }
}

/** A1 for a rectangle — the inverse, for display. */
export function formatA1Range(range: WorkbookRange): string {
  const letters = (column: number): string => {
    let rest = column
    let out = ""
    do {
      out = String.fromCharCode(65 + (rest % 26)) + out
      rest = Math.floor(rest / 26) - 1
    } while (rest >= 0)
    return out
  }
  const from = `${letters(range.startColumn)}${range.startRow + 1}`
  const to = `${letters(range.endColumn)}${range.endRow + 1}`
  return from === to ? from : `${from}:${to}`
}

/**
 * An ordinal's sheet NAME. The catalog is authoritative; a point's own `sheet` is
 * the fallback for a run whose catalog was not published. Answers null when
 * neither can name it — the honest "we cannot place this" signal.
 */
export function surfaceSheetName(
  ordinal: number,
  surfaces: DocumentSurface[] | undefined,
  fallback?: string,
): string | null {
  const named = surfaces?.find((surface) => surface.ordinal === ordinal)?.name
  return named || fallback || null
}

/** Was this surface hidden in the file the supplier sent? */
export function surfaceWasHidden(
  ordinal: number,
  surfaces: DocumentSurface[] | undefined,
): boolean {
  const surface = surfaces?.find((entry) => entry.ordinal === ordinal)
  if (!surface) return false
  return (
    surface.hidden === true ||
    surface.state === "hidden" ||
    surface.state === "veryHidden"
  )
}

/**
 * One cell proof as ONE CITATION LINE, in the shared citation vocabulary
 * (`QuoteAnchor`) the statement lists and both proof surfaces already speak:
 * `quote: null` because a rectangle of cells has no text, and the ADDRESS in
 * `summary`, which is exactly where a quote-less point's own words already go.
 *
 * WHY THIS EXISTS AT ALL. A workbook run publishes its anchors on the `cells`
 * channel and NOTHING on the flat `evidence` one (`to_evidence` excludes them by
 * design), so a surface that renders only `evidence` shows a workbook statement
 * with no evidence and no reason given. This is the adapter that lets one list
 * carry both channels.
 *
 * PURE, and free of the i18n hook like the rest of this module: the line invents
 * no prose. The sheet NAME comes from the catalog with the point's own `sheet` as
 * fallback (`surfaceSheetName`); when neither can name the surface the address
 * falls back to the bare ref, and then to the point's own summary — it never
 * prints "page 3" for a sheet.
 */
export function cellAnchorLine(
  point: CellProofPoint,
  surfaces?: DocumentSurface[],
): { quote: null; page: number; summary: string } {
  const sheet = surfaceSheetName(point.page, surfaces, point.sheet)
  const address = [sheet, point.ref].filter(Boolean).join(" ")
  return {
    quote: null,
    page: point.page,
    summary:
      address && point.summary
        ? `${address} — ${point.summary}`
        : address || (point.summary ?? ""),
  }
}

/**
 * One cell proof point → one source, in statement-list order, so the workbook
 * marks carry the same palette colours the statements show on the left. The exact
 * shape and ordering of `buildSourceRegions`, over cells instead of quotes.
 *
 * `titleOf` is the flow's run-wide statement index; the caller passes the same
 * one it gives the PDF viewer, so one statement reads one way everywhere.
 */
export function buildWorkbookSources(
  statements: WorkbookSourceStatement[],
  titleOf: (statementId: string) => string,
  surfaces?: DocumentSurface[],
): WorkbookSource[] {
  return statements.flatMap((statement, statementIndex) =>
    statement.evidence.filter(isCellProof).map((point, pointIndex) =>
      workbookSourceOf(point, {
        // Continues `buildSourceRegions`' key space rather than restarting at 0
        // — see `evidenceCount` above.
        key: `${statement.id}#${(statement.evidenceCount ?? 0) + pointIndex}`,
        statementId: statement.id,
        label: titleOf(statement.id),
        colorIndex: statementIndex % SOURCE_COLOR_COUNT,
        surfaces,
      }),
    ),
  )
}

/**
 * ONE cell proof point → one mark, resolved against the surface catalog. The whole
 * of what `buildWorkbookSources` does per point, named separately because the
 * step-3 track groups by ARTICLE rather than by statement: it walks its own units
 * and owns its own keys (`itemKey`), so it needs the resolution without the
 * statement-shaped iteration.
 *
 * `hidden` is the OR of the point's own flag and the catalog's, because either one
 * knowing the supplier hid this content is enough for the reviewer to be told.
 */
export function workbookSourceOf(
  point: CellProofPoint,
  {
    key,
    statementId,
    label,
    colorIndex,
    itemKey,
    surfaces,
  }: {
    key: string
    statementId: string
    label: string
    colorIndex: number
    itemKey?: string
    surfaces?: DocumentSurface[]
  },
): WorkbookSource {
  const sheet = surfaceSheetName(point.page, surfaces, point.sheet)
  return {
    key,
    statementId,
    label,
    colorIndex,
    ordinal: point.page,
    sheet: sheet ?? "",
    sheetKnown: sheet !== null,
    range: parseA1Range(point.ref),
    ref: point.ref ?? "",
    readBy: point.read_by,
    hidden: point.hidden === true || surfaceWasHidden(point.page, surfaces),
    producer: point.producer,
    verified: point.verified,
    summary: point.summary,
    ...(itemKey === undefined ? {} : { itemKey }),
  }
}

/**
 * Document order for a workbook: `(surface ordinal, row, col)` of the block's
 * top-left — the row-major analogue of the PDF track's `(page, y, x)`, and what
 * makes the forward/back walk feel spatial. A source with no readable range has
 * no position, so it sinks last rather than sorting as row 0.
 */
export function orderWorkbookSources(
  sources: WorkbookSource[],
): WorkbookSource[] {
  return sources
    .map((source, index) => ({ source, index }))
    .sort((a, b) => {
      const positioned =
        Number(b.source.range !== null) - Number(a.source.range !== null)
      if (positioned !== 0) return positioned
      if (a.source.range === null || b.source.range === null) {
        return a.index - b.index
      }
      return (
        a.source.ordinal - b.source.ordinal ||
        a.source.range.startRow - b.source.range.startRow ||
        a.source.range.startColumn - b.source.range.startColumn ||
        a.index - b.index
      )
    })
    .map((entry) => entry.source)
}

/** The tint fraction the PDF text highlights use, so the two feel like one app. */
const TINT = 26
const ACTIVE_TINT = 55

/**
 * The tag fields `orderCellProofs` reads off the tagged channel. Structural on
 * purpose: the read model's `TaggedAnchor` satisfies it.
 */
export type TaggedCellPoint = {
  page: number
  ref?: string
  parts?: string[]
}

/**
 * A statement's RAW cell proofs in the order its card lists and jumps them,
 * before any source key is minted. The first cell is the jump target.
 *
 *  - **Surface first.** The read model publishes the cells in citation order, and
 *    the model may cite a context block on a later sheet (a legal-text tab)
 *    first; ordering by surface ordinal keeps the jump on the answer sheet.
 *  - **Citation order inside a surface, never row order.** The model also cites
 *    the section header, the column header or the date cell above the table as
 *    context, so the topmost cell is rarely the answer.
 *  - **The first surface's first `verdict`-tagged cell leads.** The tagged
 *    channel says which cells prove the verdict; the earliest one on the first
 *    surface is the supplier's answer. Only that surface is searched: a later
 *    sheet's verdict tag is a context cell, and promoting it would undo the
 *    surface order. Without a match there (an older run, an untagged answer)
 *    the order above stands.
 *
 * A point whose range cannot be read sinks last, keeps its encounter order, and
 * never leads: there is nowhere to jump to.
 */
export function orderCellProofs<T extends CellProofPoint>(
  points: T[],
  tagged: TaggedCellPoint[] = [],
): T[] {
  const verdictCells = new Set(
    tagged
      .filter((anchor) => anchor.ref && anchor.parts?.includes("verdict"))
      .map((anchor) => `${anchor.page}!${anchor.ref}`),
  )
  const ordered = points
    .map((point, index) => ({
      point,
      index,
      positioned: parseA1Range(point.ref) !== null,
    }))
    .sort(
      (a, b) =>
        Number(b.positioned) - Number(a.positioned) ||
        (a.positioned && b.positioned ? a.point.page - b.point.page : 0) ||
        a.index - b.index,
    )
  const firstSurface = ordered[0]?.positioned ? ordered[0].point.page : null
  const answer = ordered.findIndex(
    (entry) =>
      entry.positioned &&
      entry.point.page === firstSurface &&
      verdictCells.has(`${entry.point.page}!${entry.point.ref}`),
  )
  if (answer > 0) ordered.unshift(...ordered.splice(answer, 1))
  return ordered.map((entry) => entry.point)
}

/**
 * The proof layer. `activeKey` gets the stronger tint and a border; it is also
 * the one the caller should hand to the viewer as a `jump`, because THE ACTIVE
 * PROOF IS THE NATIVE SELECTION — the decoration is the persistent tint,
 * selection is the cursor.
 *
 * A source with no sheet or no range contributes NOTHING here: there is nowhere
 * to put it. It still rides the source track, where its line says why.
 */
export function proofDecorations(
  sources: WorkbookSource[],
  activeKey: string | null,
  labels: {
    /** Badge text for content the supplier had hidden. */
    hidden: string
    /** Badge text per `read_by` value; absent ⇒ no read-by badge. */
    readBy: (readBy: string) => string | null
  },
): RangeDecoration[] {
  const marks: RangeDecoration[] = []
  for (const source of sources) {
    if (!source.sheetKnown || source.range === null) continue
    const active = source.key === activeKey
    const colour = sourceColorVar(source.colorIndex)
    // Provenance rides the mark itself: hidden content says so, and how the
    // value was read says so, both without the reviewer having to look anywhere.
    const badge = [
      source.hidden ? labels.hidden : null,
      source.readBy ? labels.readBy(source.readBy) : null,
    ]
      .filter(Boolean)
      .join(" · ")
    marks.push({
      id: `wb-proof:${source.key}`,
      sheet: source.sheet,
      range: source.range,
      fill: `color-mix(in srgb, ${colour} ${active ? ACTIVE_TINT : TINT}%, transparent)`,
      ...(active ? { border: colour } : {}),
      ...(badge ? { badge } : {}),
      className: active
        ? "wb-proof-mark wb-proof-mark-active"
        : "wb-proof-mark",
    })
  }
  // The active mark last, so its stronger tint paints over any it overlaps.
  return marks.sort(
    (a, b) =>
      Number(a.className?.includes("active")) -
      Number(b.className?.includes("active")),
  )
}
